<template>
    <div class="mx-auto max-w-screen-2xl h-full grid sm:grid-cols-3 gap-12">
        <div class="col-span-2">
            <h3 class="text-2xl tracking-widest uppercase font-bold text-gray-700 mb-4">Recent Emails Received</h3>
            <div class="bg-gray-50 shadow overflow-hidden sm:rounded-md border border-gray-200" v-if="Object.keys(mail).length > 0">
                <ul role="list" class="divide-y divide-gray-300">
                    <li v-for="(events, subject) in mail" :key="subject">
                        <a href="#" class="block bg-white hover:bg-gray-100">
                            <div class="flex items-center px-4 py-4 sm:px-6">
                                <div class="min-w-0 flex-1 flex items-center">
                                    <div class="min-w-0 flex-1 px-4">
                                        <div>
                                            <p class="text-lg text-gray-800 truncate">
                                                {{ subject }}
                                            </p>
                                            <p class="mt-2 text-sm">
                                                {{ formatDateShort(events.find(o => o.type === 'injection')?.timestamp)  }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div class="-mt-px flex divide-x divide-gray-200">
                                    <div class="w-0 flex-1 flex">
                                        <a href="#" class="relative -mr-px w-0 flex-1 inline-flex items-center justify-center py-4 text-sm text-gray-700 font-medium border border-transparent rounded-bl-lg hover:text-gray-500">
                                            <template v-if="events.find(o => o.type === 'injection')">
                                                <CheckCircleIcon class="h-5 w-5 text-green-400" aria-hidden="true" />
                                            </template>
                                            <template v-else>
                                                <XCircleIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
                                            </template>
                                            <span class="ml-3">Sent</span>
                                        </a>
                                    </div>
                                    <div class="w-0 flex-1 flex">
                                        <a href="#" class="relative -mr-px w-0 flex-1 inline-flex items-center justify-center py-4 text-sm text-gray-700 font-medium border border-transparent rounded-bl-lg hover:text-gray-500">
                                            <template v-if="events.find(o => o.type === 'delivery')">
                                                <CheckCircleIcon class="h-5 w-5 text-green-400" aria-hidden="true" />
                                            </template>
                                            <template v-else-if="events.find(o => o.type === 'bounce')">
                                                <XCircleIcon class="h-5 w-5 text-red-400" aria-hidden="true" />
                                            </template>
                                            <template v-else>
                                                <XCircleIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
                                            </template>
                                            <span class="ml-3">Received</span>
                                        </a>
                                    </div>
                                    <div class="w-0 flex-1 flex">
                                        <a href="#" class="relative -mr-px w-0 flex-1 inline-flex items-center justify-center py-4 text-sm text-gray-700 font-medium border border-transparent rounded-bl-lg hover:text-gray-500">
                                            <template v-if="events.find(o => o.type === 'open')">
                                                <CheckCircleIcon class="h-5 w-5 text-green-400" aria-hidden="true" />
                                            </template>
                                            <template v-else>
                                                <XCircleIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
                                            </template>
                                            <span class="ml-3">Opened</span>
                                        </a>
                                    </div>
                                    <div class="w-0 flex-1 flex">
                                        <a href="#" class="relative -mr-px w-0 flex-1 inline-flex items-center justify-center py-4 text-sm text-gray-700 font-medium border border-transparent rounded-bl-lg hover:text-gray-500">
                                            <template v-if="events.find(o => o.type === 'spam_complaint')">
                                                <ExclamationCircleIcon class="h-5 w-5 text-amber-400" aria-hidden="true" />
                                            </template>
                                            <template v-else>
                                                <XCircleIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
                                            </template>
                                            <span class="ml-3">Spam Report</span>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </a>
                    </li>
                </ul>
            </div>
            <div v-if="mail.length === 0">
                <p class="text-sm text-gray-400">Our mailing provider has no records for this recipient.</p>
            </div>
        </div>
        <div class="col-span-1">
            <h3 class="text-2xl tracking-widest uppercase font-bold text-gray-700 mb-4">Preferences</h3>

            <SwitchGroup as="div" class="flex items-center mb-2" v-for="(preference, label) in preferences">
                <Switch v-model="state.profile[preference]" :class="[state.profile[preference] ? 'bg-indigo-600' : 'bg-gray-200', 'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500']">
                    <span aria-hidden="true" :class="[state.profile[preference] ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200']" />
                </Switch>
                <SwitchLabel as="span" class="ml-3">
                    <span class="text-sm font-medium text-gray-900">{{ label }}</span>
                </SwitchLabel>
            </SwitchGroup>

            <button type="submit" @click="saveState" class="mt-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                <template v-if="saveSuccessful"><CheckCircleIcon class="h-5 w-5 text-white animate-spin-once mr-4" /></template>
                Save
            </button>
        </div>
    </div>
</template>

<script>
import {ref, toRef, onMounted} from "vue"
import {CheckCircleIcon, XCircleIcon} from "@heroicons/vue/24/outline"
import {ExclamationCircleIcon} from "@heroicons/vue/24/solid"
import {Switch, SwitchGroup, SwitchLabel} from "@headlessui/vue"
import {useBus} from "../../../../app"

const preferences = {
    'Newsletter': 'mailing_newsletter',
    'Media Requests': 'mailing_mediarequests',
    'Media Request Receipts': 'mailing_mediarequestreceipts',
}

export default {
    name: "Mailing",
    components: {
        CheckCircleIcon,
        XCircleIcon,
        ExclamationCircleIcon,
        SwitchGroup,
        Switch,
        SwitchLabel,
    },
    props: {
        user: {
            type: Object,
            required: true,
        }
    },
    setup(props) {
        const bus = useBus()
        const mail = ref([])
        const state = toRef(props, 'user')
        const saveSuccessful = ref(false)

        const getSparkpostMails = async () => {
            await axios.get(route('users.mailing.events', { user: props.user.id }))
                .then((response) => {
                    let results = _.groupBy(response.data.results, 'subject');

                    if (Object.keys(results).length > 0) {
                        mail.value = results
                    }
                })
        }

        const saveState = async () => {
            await axios.post(route('users.save', state.value.id), {
                user: state.value,
                profile: state.value.profile,
            }).then((response) => {
                if (response.status === 200) {
                    bus.$emit('alert.success', 'User Updated Successfully')
                    saveSuccessful.value = true
                }
            })
        }

        onMounted(() => {
            window.location.hash = 'mail'
            getSparkpostMails()
        })

        return {
            mail,
            state,
            preferences,
            saveState,
            saveSuccessful,
        }
    }
}
</script>

<style scoped>

</style>
